import React from 'react';
import { Tabs, Tab, Link, Box, styled, Typography, CircularProgress, FormControl, Input, InputAdornment, Button, IconButton, Stack, Dialog, Grid, Paper, Divider, Tooltip, LinearProgress } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import SimpleBar from 'simplebar-react';
import { ChangeEvent } from 'react';

const ui: any = {
    PageContainer: styled(Box)(({ theme }) => ({
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#F0F4F8',
        overflow: 'hidden',
        position: 'relative',
    })),

    PageHeader: styled(Box)(({ theme }) => ({
        backgroundColor: '#FFFFFF',
        borderRadius: '14px',
        border: '1px solid #DDE4EC',
        boxShadow: '0 1px 3px rgba(13,27,42,0.05), 0 4px 16px rgba(13,27,42,0.04)',
        margin: `${theme.spacing(2)} ${theme.spacing(2.5)} 0`,
        padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexShrink: 0,
        minHeight: '60px',
    })),

    GridTabsBar: styled(Box)(({ theme }) => ({
        backgroundColor: '#FFFFFF',
        borderRadius: '14px',
        border: '1px solid #DDE4EC',
        margin: `${theme.spacing(1.5)} ${theme.spacing(2.5)} 0`,
        flexShrink: 0,
    })),

    PageTitle: styled(Typography)(({ theme }) => ({
        display: 'inline-block',
        fontSize: '20px',
        fontWeight: 700,
        color: '#0D1B2A',
        letterSpacing: '-0.015em',
        lineHeight: 1.2,
        [theme.breakpoints.up('sm')]: { flex: 2 },
        [theme.breakpoints.down('sm')]: { flex: 0.5 },
    })),

    PageSubtitle: styled(Typography)({
        fontSize: '12px',
        color: '#8B99A6',
        fontWeight: 400,
        marginTop: '2px',
    }),

    PageActions: styled(Stack)(({ theme }) => ({
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(1.5),
    })),

    PageContent: styled(Box)(({ theme }) => ({
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: `${theme.spacing(1.5)} ${theme.spacing(2.5)} ${theme.spacing(2.5)}`,
    })),

    ActionButton: styled(Button)(({ theme }) => ({
        marginLeft: theme.spacing(2),
        minWidth: '150px',
        height: '38px',
        border: '1px solid #0891B2',
        borderRadius: '8px',
        color: '#0891B2',
        fontWeight: 600,
        fontSize: '13px',
        textTransform: 'none',
        '&:hover': {
            border: '1px solid #0E7490',
            color: '#0E7490',
            backgroundColor: '#F0FAFB',
        },
    })),

    SearchInput: styled(Input)(({ theme }) => ({
        backgroundColor: '#F8FAFC',
        border: '1px solid #DDE4EC',
        borderRadius: '8px',
        padding: `0 ${theme.spacing(1.5)}`,
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:before': { display: 'none' },
        '&:after': { display: 'none' },
        '&:hover:not(.Mui-disabled)': { borderColor: '#0891B2' },
        '&.Mui-focused': {
            borderColor: '#0891B2',
            boxShadow: '0 0 0 3px rgba(8,145,178,0.12)',
        },
        [theme.breakpoints.up('sm')]: { minWidth: '300px' },
        '& input': {
            height: '36px',
            padding: 0,
            fontSize: '14px',
            color: '#374151',
        },
    })),

    Table: styled(Box)(({ theme }) => ({
        minWidth: 'var(--table-total-width, 100%)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: '10px',
        boxShadow: '0 1px 3px rgba(13,27,42,0.05), 0 4px 16px rgba(13,27,42,0.04)',
    })),

    TableHead: styled(Box)(({ theme }) => ({
        backgroundColor: '#F8FAFC',
        borderBottom: '1px solid #E2E8F0',
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        zIndex: 2,
    })),

    TableCell: styled(Box)(({ theme }) => ({
        flex: 'none',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'row',
        minHeight: '52px',
        alignItems: 'center',
        fontSize: '13px',
        color: '#374151',
        padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    })),

    TableHeader: styled(Box)(({ theme }) => ({
        flex: 'none',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        minHeight: '42px',
        padding: `0 ${theme.spacing(1)}`,
        fontWeight: 700,
        fontSize: '10.5px',
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: '#8B99A6',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        position: 'relative',
        userSelect: 'none',
    })),

    ResizeHandle: styled(Box)({
        position: 'absolute',
        right: 0,
        top: '15%',
        height: '70%',
        width: '8px',
        cursor: 'col-resize',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
        '&::after': {
            content: '""',
            display: 'block',
            width: '1.5px',
            height: '100%',
            backgroundColor: '#DDE4EC',
            borderRadius: '2px',
            transition: 'background-color 0.15s, width 0.15s',
        },
        '&:hover::after': {
            backgroundColor: '#0891B2',
            width: '2.5px',
        },
    }),

    TableBody: styled(Box)(({ theme }) => ({
        flex: 1,
    })),

    TableLoadingBar: styled(LinearProgress)({
        height: '2px',
        flexShrink: 0,
        backgroundColor: 'rgba(8,145,178,0.12)',
        '& .MuiLinearProgress-bar': {
            backgroundColor: '#0891B2',
        },
    }),

    TableRow: styled(Box)(({ theme }) => ({
        width: 'var(--table-total-width, 100%)',
        minHeight: '52px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'row',
    })),

    TableRowAlt: styled(Box)(({ theme }) => ({
        width: 'var(--table-total-width, 100%)',
        minHeight: '52px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'row',
    })),

    ScrollBar: styled(SimpleBar)(({ theme }) => ({
        flex: 1,
        minHeight: 0,
        '& .simplebar-content': { height: '100%' },
    })) as any,

    DetailPane: styled(Dialog)(({ theme }) => ({
        '& .MuiDialog-paper': {
            width: '92vw',
            maxWidth: '1180px',
            height: '86vh',
            maxHeight: '860px',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 8px 28px rgba(13,27,42,0.20), 0 2px 8px rgba(13,27,42,0.10)',
        },
        '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(13,27,42,0.48)',
        },
    })),

    DetailPaneContent: styled(Box)(({ theme }) => ({
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'white',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
    })),

    DetailsPaneTabs: styled(Tabs)(({ theme }) => ({
        borderBottom: '1px solid #E2E8F0',
        minHeight: 'unset',
        paddingLeft: theme.spacing(2.5),
        paddingRight: theme.spacing(2.5),
        flexShrink: 0,
        '& .MuiTabs-indicator': {
            height: '2.5px',
            borderRadius: '2px',
            backgroundColor: '#0891B2',
        },
    })),

    DetailPaneTab: styled(Tab)(({ theme }) => ({
        marginRight: theme.spacing(3),
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(0.5),
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(1.5),
        fontSize: '13px',
        fontWeight: 600,
        minHeight: 'unset',
        minWidth: 0,
        textTransform: 'none',
        color: '#8B99A6',
        backgroundColor: 'transparent',
        '&:hover': { color: '#374151' },
        '&.Mui-selected': {
            color: '#0891B2',
        },
        '& .MuiTab-iconWrapper': {
            marginRight: '6px',
            fontSize: '16px',
        },
    })),

    DetailTabPanel: styled(Box)<DetailTabPanelProps>(({ theme, $show }) => ({
        flex: 1,
        minHeight: 0,
        display: !$show ? 'none' : 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    })),

    OrderDetailActions: styled(Box)(({ theme }) => ({
        minWidth: 'fit-content',
        paddingRight: theme.spacing(4),
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: theme.spacing(4),
        width: '215px',
    })),

    OrderDetailAction: styled(Button)(({ theme }) => ({
        border: '2px solid #0891B2',
        borderRadius: '5px',
        height: '110px',
        width: '150px',
        boxSizing: 'border-box',
        marginLeft: 'auto',
        marginRight: 'auto',
        textTransform: 'uppercase',
        color: '#0891B2',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-around',
        textAlign: 'center',
        fontWeight: 600,
        fontSize: '16px',
    })),

    OrderDetailActionLink: styled(RouterLink)(({ theme }) => ({
        textDecoration: 'none',
        border: '2px solid #0891B2',
        borderRadius: '5px',
        height: '110px',
        width: '150px',
        boxSizing: 'border-box',
        marginLeft: 'auto',
        marginRight: 'auto',
        textTransform: 'uppercase',
        color: '#0891B2',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-around',
        textAlign: 'center',
        fontWeight: 600,
        fontSize: '16px',
        'svg': { alignSelf: 'center' },
        cursor: 'pointer',
        paddingBottom: theme.spacing(2),
    })),

    OrderDetails: styled(Box)(({ theme }) => ({})),

    OrderButtons: styled(Box)(({ theme }) => ({
        flex: 1,
        alignSelf: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-evenly',
    })),

    OrderDetailOrderNo: styled(Typography)(({ theme }) => ({
        marginTop: 0,
        fontSize: '24px',
        fontWeight: 400,
    })),

    OrderDetailPatientName: styled(Typography)(({ theme }) => ({
        marginTop: theme.spacing(1.5),
        fontSize: '24px',
        fontWeight: 400,
        maxWidth: '183px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    })),

    OrderDetailContent: styled(Box)(({ theme }) => ({
        flex: 1,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        paddingBottom: theme.spacing(3),
        height: '100%',
    })),

    OrderDetailInfo: styled(Grid)(({ theme }) => ({
        fontSize: '12px',
        flex: 1,
        height: 'max-content',
        marginBottom: theme.spacing(0),
        display: 'flex',
        flexDirection: 'row',
        alignContent: 'baseline',
    })),

    OrderDetailSamples: styled(Grid)(({ theme }) => ({
        fontSize: '12px',
        flex: 1,
        height: '100%',
        marginBottom: theme.spacing(2),
        display: 'flex',
        flexDirection: 'row',
    })),

    OrderDetailItem: styled(Grid)(({ theme }) => ({
        border: '1px solid #B6B6B6',
        boxSizing: 'border-box',
        height: '26px',
    })),

    OrderDetailLabel: styled(Box)(({ theme }) => ({
        display: 'flex',
        alignItems: 'center',
        flex: 1.5,
        backgroundColor: '#D9D9D9',
        padding: theme.spacing(1.5),
        height: '100%',
    })),

    OrderDetailValue: styled(Box)(({ theme }) => ({
        display: 'inline-block',
        alignItems: 'center',
        flex: 2,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        backgroundColor: '#F7F5F2',
        padding: theme.spacing(1.5),
        fontWeight: 600,
        height: '100%',
        lineHeight: 0,
    })),

    OrderMessage: styled(Paper)(({ theme }) => ({
        padding: theme.spacing(2),
    })),

    OrderMessageBody: styled(Box)(({ theme }) => ({})),

    OrderMessageSender: styled(Box)(({ theme }) => ({
        fontWeight: 600,
    })),

    OrderAttachmentsAction: styled(Button)(({ theme }) => ({
        border: '2px solid #0891B2',
        borderRadius: '5px',
        height: '110px',
        width: '150px',
        boxSizing: 'border-box',
        marginLeft: 'auto',
        marginRight: 'auto',
        textTransform: 'uppercase',
        color: '#0891B2',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-around',
        textAlign: 'center',
        fontWeight: 600,
        fontSize: '16px',
    })),

    OrderAttachmentsActions: styled(Box)(({ theme }) => ({})),

    OrderAttachmentsContent: styled(Box)(({ theme }) => ({
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: theme.spacing(15),
        minWidth: '50%',
    })),

    OrderAttachmentsTitle: styled(Typography)(({ theme }) => ({})),

    OrderAttachmentsDivider: styled(Divider)(({ theme }) => ({})),

    ReportsTable: styled(Box)(({ theme }) => ({
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        padding: theme.spacing(2),
    })),

    ReportsTableHead: styled(Box)(({ theme }) => ({
        display: 'flex',
    })),

    ReportsTableHeader: styled(Box)(({ theme }) => ({
        padding: theme.spacing(1),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(20),
        fontWeight: 600,
        borderBottom: '2px solid #E9E9E9',
        width: '350px',
    })),

    ReportsTableBody: styled(Box)(({ theme }) => ({
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
    })),

    ReportsTableBodyRow: styled(Box)(({ theme }) => ({
        display: 'flex',
        width: '100%',
    })),

    ReportsTableAction: styled(Button)(({ theme }) => ({
        color: '#0891B2',
        textTransform: 'uppercase',
        fontWeight: 600,
        marginRight: theme.spacing(6),
        lineHeight: 'unset',
        fontSize: '1rem',
        padding: theme.spacing(0),
    })),

    ReportsTableLink: styled(Link)(({ theme }) => ({
        color: '#0891B2',
        textTransform: 'uppercase',
        fontWeight: 600,
        paddingRight: theme.spacing(6),
    })),

    ReportsTableCell: styled(Box)(({ theme }) => ({
        padding: theme.spacing(2),
        fontWeight: 400,
        width: '350px',
    })),

    OrderAttachmentsRow: styled(Box)(({ theme }) => ({
        padding: theme.spacing(2),
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
        maxHeight: '75px',
    })),

    OrderAttachmentsFile: styled(Box)(({ theme }) => ({})),

    OrderAttachmentsFileActions: styled(Box)(({ theme }) => ({
        marginLeft: 'auto',
    })),

    OrderAttachmentsFileLink: styled(Link)(({ theme }) => ({
        color: '#0891B2',
        textTransform: 'uppercase',
        fontWeight: 600,
        paddingRight: theme.spacing(6),
    })),

    OrderAttachmentsFileView: styled(Button)(({ theme }) => ({
        color: '#0891B2',
        textTransform: 'uppercase',
        fontWeight: 600,
        marginRight: theme.spacing(5),
        lineHeight: 'unset',
        fontSize: '1rem',
        padding: theme.spacing(0),
    })),

    CreateOrderButton: styled(RouterLink)(({ theme }) => ({
        cursor: 'pointer',
        fontSize: '13px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        backgroundColor: '#0891B2',
        border: '1px solid #0891B2',
        color: '#FFFFFF',
        fontWeight: 600,
        padding: `0 ${theme.spacing(2.5)}`,
        height: '38px',
        minWidth: '160px',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        letterSpacing: '0.01em',
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: '#0E7490',
            borderColor: '#0E7490',
            textDecoration: 'none',
        },
    })),

    SuspendedCreateOrderButton: styled(RouterLink)(({ theme }) => ({
        cursor: 'not-allowed',
        fontSize: '13px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        backgroundColor: '#F3F4F6',
        border: '1px solid #D1D5DB',
        color: '#9CA3AF',
        fontWeight: 600,
        padding: `0 ${theme.spacing(2.5)}`,
        height: '38px',
        minWidth: '160px',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        '&:hover': { textDecoration: 'none' },
    })),

    StyledTooltip: styled(({ className, ...props }: any) => (
        <Tooltip {...props} arrow classes={{ popper: className }} />
    ))(({ theme }) => ({
        '& .MuiTooltip-tooltip': {
            backgroundColor: '#1E293B',
            color: '#FFFFFF',
            fontSize: '12.5px',
            fontWeight: 500,
            lineHeight: 1.45,
            borderRadius: '8px',
            padding: '10px 12px',
            maxWidth: '240px',
            boxShadow: '0 8px 24px rgba(13,27,42,0.24)',
        },
        '& .MuiTooltip-arrow': {
            color: '#1E293B',
        },
    })),

    // Wider variant of StyledTooltip for rich, multi-paragraph notices
    // (billing/suspension messaging) that need room to breathe.
    WideTooltip: styled(({ className, ...props }: any) => (
        <Tooltip {...props} arrow classes={{ popper: className }} />
    ))(({ theme }) => ({
        '& .MuiTooltip-tooltip': {
            backgroundColor: '#1E293B',
            color: '#FFFFFF',
            borderRadius: '10px',
            padding: 0,
            maxWidth: '340px',
            boxShadow: '0 8px 24px rgba(13,27,42,0.24)',
        },
        '& .MuiTooltip-arrow': {
            color: '#1E293B',
        },
    })),

    NoticeContent: styled(Box)({
        display: 'flex',
        flexDirection: 'column',
        padding: '14px 16px',
        gap: '8px',
    }),

    NoticeHeader: styled(Box)({
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '13px',
        fontWeight: 700,
        color: '#FFFFFF',
        letterSpacing: '-0.005em',
        '& svg': { fontSize: '17px', color: '#FBBF24', flexShrink: 0 },
    }),

    NoticeBody: styled(Box)({
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: 1.55,
        color: '#CBD5E1',
        '& strong': { color: '#FFFFFF', fontWeight: 600 },
    }),

    NoticeDivider: styled(Box)({
        height: '1px',
        backgroundColor: 'rgba(255,255,255,0.12)',
        margin: '2px 0',
    }),

    NoticeContact: styled(Box)({
        fontSize: '11.5px',
        lineHeight: 1.6,
        color: '#94A3B8',
        '& a': {
            color: '#5EC8E0',
            fontWeight: 600,
            textDecoration: 'none',
        },
        '& a:hover': { textDecoration: 'underline' },
    }),

    NoticeFooter: styled(Box)({
        fontSize: '11px',
        lineHeight: 1.5,
        color: '#64748B',
        marginTop: '2px',
    }),

    GridLoading: styled(Box)(({ theme }) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        paddingTop: '10%',
        paddingBottom: '10%',
        width: '100%',
    })),

    GridLoadingSpinner: styled(CircularProgress)(({ theme }) => ({
        color: '#0891B2',
    })),

    GridLoadingText: styled(Typography)(({ theme }) => ({
        paddingTop: theme.spacing(3),
        display: 'block',
    })),

    GridEmpty: styled(Box)(({ theme }) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        paddingTop: '10%',
        paddingBottom: '10%',
        width: '100%',
    })),

    GridEmptyText: styled(Typography)(({ theme }) => ({
        paddingTop: theme.spacing(3),
        display: 'block',
    })),
};

ui.GridLoadingText.defaultProps = { variant: 'h5' };
ui.GridLoadingSpinner.defaultProps = { size: 80 };
ui.OrderDetailActionLink.defaultProps = { underline: 'none' };
ui.OrderAttachmentsFileLink.defaultProps = { underline: 'none' };
ui.ReportsTableLink.defaultProps = { underline: 'none' };

ui.ReportsTableCellFlex = styled(ui.ReportsTableCell)(({ theme }) => ({ flex: 1 }));
ui.ReportsTableHeaderFlex = styled(ui.ReportsTableHeader)(({ theme }) => ({ width: 'unset', flex: 1 }));

ui.OrderAttachmentsTitle.defaultProps = { variant: 'h6' };

ui.OrderMessageTabPanel = styled(ui.DetailTabPanel)(({ theme }) => ({
    flexDirection: 'row',
}));

ui.OrderMessage.defaultProps = { elevation: 3 };
ui.OrderDetailOrderNo.defaultProps = { variant: 'h2' };
ui.OrderDetailPatientName.defaultProps = { variant: 'h2' };

ui.OrderDetailInfo.defaultProps = {
    container: true,
    spacing: 0,
    padding: 0,
    rowGap: 0,
    columnSpacing: 0,
};

ui.OrderDetailItem.defaultProps = {
    item: true,
    xs: 3,
    display: 'flex',
    flexDirection: 'row',
};

ui.TableBodyRow = styled(ui.TableRow, { shouldForwardProp: prop => prop !== 'orderstatus' })<TableBodyRowProps & { orderstatus?: string }>(
    ({ selected, orderstatus }: any) => {
        const stripeMap: Record<string, string> = {
            'Open':               '#D97706',
            'In Lab':             '#2563EB',
            'Completed':          '#059669',
            'Needs Contact':      '#DC2626',
            'Canceled Voided':    '#64748B',
            'Canceled Damaged':   '#BE185D',
            'Canceled Unuseable': '#7C3AED',
        };
        const stripe = stripeMap[orderstatus ?? ''] ?? '#CBD5E1';
        return {
            boxSizing: 'border-box',
            borderLeft: `3px solid ${stripe}`,
            borderBottom: '1px solid #F1F5F9',
            cursor: 'pointer',
            transition: 'background-color 0.12s ease',
            '&:hover': { backgroundColor: '#F8FAFC' },
            ...(selected ? {
                backgroundColor: `${stripe}1A`,
                outline: `1px solid ${stripe}`,
                outlineOffset: '-1px',
            } : {}),
        };
    }
);

ui.TableBodyRowAlt = ui.TableBodyRow;

ui.DetailsPaneTabs.defaultProps = { indicatorColor: '' };
ui.DetailPaneTab.defaultProps = { iconPosition: 'start' };
ui.ScrollBar.defaultProps = { autoHide: false };

ui.PageActions.defaultProps = { direction: 'row' };

ui.FilterGridButton = styled(Button)(({ theme }) => ({
    display: 'inline-flex',
    borderRadius: '8px',
    border: '1px solid #DDE4EC',
    backgroundColor: '#FFFFFF',
    height: '38px',
    minWidth: '110px',
    padding: `0 ${theme.spacing(2)}`,
    fontWeight: 600,
    fontSize: '13px',
    color: '#4B5563',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    transition: 'border-color 0.15s ease',
    '&:hover': {
        border: '1px solid #0891B2',
        color: '#0891B2',
        backgroundColor: '#F0FAFB',
    },
    '& .MuiButton-endIcon': { marginLeft: theme.spacing(0.5) },
    '& svg': { fontSize: '1.2rem', color: 'inherit' },
}));

ui.FilterGridButton.defaultProps = {
    startIcon: <FilterListIcon />,
    children: <span>Filter</span>,
};

ui.RefreshButton = styled(IconButton)(({ theme }) => ({
    width: '38px',
    height: '38px',
    border: '1px solid #DDE4EC',
    borderRadius: '8px',
    color: '#4B5563',
    flexShrink: 0,
    '&:hover': {
        border: '1px solid #0891B2',
        color: '#0891B2',
        backgroundColor: '#F0FAFB',
    },
    '& svg': { fontSize: '1.2rem' },
}));

export type TableBodyRowProps = {
    selected?: boolean;
}

export type DetailTabPanelProps = {
    $show?: boolean;
}

export type SearchFieldProps = {
    label?: string;
    value?: any;
    onChange?: any;
    onKeyDown?: any;
    onClear?: () => void;
    placeholder?: string;
    loading?: boolean;
}

export const SearchField: React.FC<SearchFieldProps> = ({ label, loading, onClear, ...muiProps }) => (
    <FormControl sx={{ pr: 0 }} variant="filled">
        <ui.SearchInput
            placeholder={label ?? 'Start Searching...'}
            endAdornment={
                <InputAdornment position='end'>
                    {loading
                        ? <CircularProgress size={16} thickness={5} sx={{ color: '#0891B2' }} />
                        : muiProps.value
                            ? <IconButton size='small' onClick={onClear} sx={{ p: '2px', color: '#9CA3AF', '&:hover': { color: '#374151' } }}>
                                <CloseIcon sx={{ fontSize: '16px' }} />
                              </IconButton>
                            : <SearchIcon sx={{ fontSize: '18px', color: '#9CA3AF' }} />
                    }
                </InputAdornment>
            }
            {...muiProps}
        />
    </FormControl>
);

export { ui };
